import { forwardRef, type JSX, useImperativeHandle, useRef } from 'react'
import { useSelector } from 'react-redux'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { Box, IconButton, TableCell } from '@mui/material'
import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import { LanguageUtils, Text } from '@pbt/pbt-ui-components'
import { Delete as DeleteIcon } from '@pbt/pbt-ui-components/src/icons'

import type { SoapTemplateWidget } from '~/api/graphql/generated/types'
import DragAndDropTable from '~/components/common/lists/DragAndDropTable'
import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'
import { type SoapTemplateTab, SoapWidgetName } from '~/types'

import NotesWidgetConfigDetails from './config-details/NotesWidgetConfigDetails'

const useStyles = makeStyles(
  (theme) => ({
    deleteIcon: {
      marginLeft: 'auto',
      color: theme.colors.lowAccentText,
    },
    dragIndicatorIcon: {
      color: theme.colors.lowAccentText,
    },
    removeBorder: {
      border: 'none',
      borderTop: 'none !important',
    },
    table: {
      margin: theme.spacing(0, -3),
      width: `calc(100% + ${theme.spacing(3 * 2)})`,
    },
    tableCell: {
      padding: theme.spacing(0, 1.5, 0, 3),
      height: 40,
    },
  }),
  { name: 'SoapWidgetsCandidate' },
)

const SoapWidgetConfigMap: Partial<
  Record<SoapWidgetName, (props: any) => JSX.Element | null>
> = {
  [SoapWidgetName.NOTES_WIDGET]: NotesWidgetConfigDetails,
}

export interface SoapWidgetsCandidateProps {
  allTabs: SoapTemplateTab[]
  onDelete: (item: SoapTemplateWidget) => void
  onNavigateAway?: () => void
  onOrderChange: (sourceIndex: number, targetIndex: number) => void
  onUpdateWidgetConfig: (changes: any) => void
  tabId?: string
  widgets: (SoapTemplateWidget & { name: string; nameTranslation: string })[]
}

export interface SoapWidgetsCandidateHandle {
  validate: () => boolean
}

export interface ConfigDetailsHandle {
  validate: () => boolean
}

const SoapWidgetsCandidate = forwardRef(
  (
    {
      onDelete,
      onOrderChange,
      widgets,
      onUpdateWidgetConfig,
      tabId,
      allTabs,
      onNavigateAway,
    }: SoapWidgetsCandidateProps,
    ref,
  ) => {
    const classes = useStyles()

    const isDefaultSoapNotesTemplatesEnabled = useSelector(
      getFeatureToggle(FeatureToggle.SET_DEFAULT_SOAP_NOTE_TEMPLATES),
    )

    const widgetsRefs = useRef<Record<string, ConfigDetailsHandle>>({})

    useImperativeHandle(ref, () => ({
      validate: () =>
        Object.values(widgetsRefs.current).every(
          (widgetRef) => widgetRef == null || widgetRef.validate(),
        ),
    }))

    return (
      <DragAndDropTable
        useIndexAsId
        classes={{
          tableRow: classes.removeBorder,
          table: classes.removeBorder,
          root: classes.table,
        }}
        data={widgets}
        onOrderChange={onOrderChange}
      >
        {({ item }) => {
          const WidgetConfigComponent =
            SoapWidgetConfigMap[item.name as SoapWidgetName]
          return (
            <TableCell
              className={classNames(classes.removeBorder, classes.tableCell)}
            >
              <Box
                alignItems="center"
                display="flex"
                justifyContent="space-between"
              >
                <DragIndicatorIcon className={classes.dragIndicatorIcon} />
                <Text>{LanguageUtils.getTranslatedFieldName(item)}</Text>
                <IconButton
                  className={classes.deleteIcon}
                  id="delete-widget-button"
                  onClick={() => {
                    delete widgetsRefs.current[item.soapWidgetId]
                    onDelete(item)
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              {isDefaultSoapNotesTemplatesEnabled && WidgetConfigComponent && (
                <WidgetConfigComponent
                  allTabs={allTabs}
                  item={item}
                  ref={(el: ConfigDetailsHandle) => {
                    widgetsRefs.current[item.soapWidgetId] = el
                  }}
                  soapWidgetId={item.soapWidgetId}
                  tabId={tabId}
                  onNavigateAway={onNavigateAway}
                  onUpdateWidgetConfig={onUpdateWidgetConfig}
                />
              )}
            </TableCell>
          )
        }}
      </DragAndDropTable>
    )
  },
)

export default SoapWidgetsCandidate
